import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Mail, MapPin, Calendar, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    interests: [],
    challenge: '',
    urgency: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const interestOptions = [
    { id: 'data-engineering', label: 'Data Engineering' },
    { id: 'cloud-engineering', label: 'Cloud Engineering' },
    { id: 'ai-services', label: 'AI Services' },
    { id: 'talent-pods', label: 'Engineering Talent Pods' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'other', label: 'Other' }
  ];

  const urgencyOptions = [
    { value: 'Exploring', label: 'Exploring' },
    { value: 'Looking to start in 1-3 months', label: 'Looking to start in 1-3 months' },
    { value: 'Need help ASAP', label: 'Need help ASAP' }
  ];

  const handleInterestChange = (interestId, checked) => {
    setFormData(prev => ({
      ...prev,
      interests: checked 
        ? [...prev.interests, interestId]
        : prev.interests.filter(id => id !== interestId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.role || 
        formData.interests.length === 0 || !formData.challenge || !formData.urgency) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="contact-page">
        <section className="section-padding bg-secondary/30 min-h-[60vh] flex items-center">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-xl mx-auto text-center"
            >
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h1 className="heading-2 text-foreground mb-4">
                Thank you for reaching out!
              </h1>
              <p className="body-lg mb-8">
                We've received your inquiry and will get back to you within 1-2 business days 
                with concrete next steps.
              </p>
              <Button 
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    company: '',
                    role: '',
                    interests: [],
                    challenge: '',
                    urgency: ''
                  });
                }}
                variant="outline"
                data-testid="submit-another-btn"
              >
                Submit another inquiry
              </Button>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <Helmet>
        <title>Contact Us - Cynergy Services</title>
        <meta name="description" content="Tell us about your data, cloud or AI challenge. Contact Cynergy Services for a consultation." />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding bg-secondary/30" data-testid="contact-hero">
        <div className="container-custom">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeInUp}
            className="max-w-2xl"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
              Contact Us
            </p>
            <h1 className="heading-1 text-foreground mb-6">
              Tell us about your challenge
            </h1>
            <p className="body-lg">
              Share a bit about your goals and current situation, and we'll respond 
              with concrete next steps.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-white" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2"
            >
              <Card className="border-border/60">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Work Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company">Company *</Label>
                        <Input
                          id="company"
                          placeholder="Your company"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          required
                          data-testid="contact-company-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="role">Role *</Label>
                        <Input
                          id="role"
                          placeholder="Your role"
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          required
                          data-testid="contact-role-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label>What are you interested in? *</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {interestOptions.map((option) => (
                          <div key={option.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={option.id}
                              checked={formData.interests.includes(option.id)}
                              onCheckedChange={(checked) => handleInterestChange(option.id, checked)}
                              data-testid={`interest-${option.id}`}
                            />
                            <Label htmlFor={option.id} className="text-sm font-normal cursor-pointer">
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="challenge">Brief description of your challenge *</Label>
                      <Textarea
                        id="challenge"
                        placeholder="Tell us about your current situation, goals and any specific challenges..."
                        rows={4}
                        value={formData.challenge}
                        onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                        required
                        data-testid="contact-challenge-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="urgency">How urgent is this? *</Label>
                      <Select 
                        value={formData.urgency} 
                        onValueChange={(value) => setFormData({ ...formData, urgency: value })}
                      >
                        <SelectTrigger data-testid="contact-urgency-select">
                          <SelectValue placeholder="Select urgency level" />
                        </SelectTrigger>
                        <SelectContent>
                          {urgencyOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full btn-primary"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="space-y-6">
                <Card className="border-border/60" data-testid="contact-details-card">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Contact details
                    </h3>
                    <div className="space-y-4">
                      <a 
                        href="mailto:info@cynergyservices.org"
                        className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors duration-200"
                        data-testid="contact-email-link"
                      >
                        <Mail className="w-5 h-5" />
                        <span>info@cynergyservices.org</span>
                      </a>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 text-muted-foreground" data-testid="contact-location-india">
                          <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium mb-2 text-foreground">India</p>
                            <p className="text-sm mb-2">Noida, Uttar Pradesh, 201306</p>
                      
                            <p className="text-sm">Aerospace Park, near Shell Office, Huvinayakanahalli, Bengaluru, Karnataka 562149, India</p>
                          
                          </div>
                        </div>
                        <div className="flex items-start gap-3 text-muted-foreground" data-testid="contact-location-singapore">
                          <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Singapore</p>
                            <p className="text-sm">Melville Park, Singapore, 529942</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/60 bg-primary/5" data-testid="book-call-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Calendar className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Prefer to talk live?
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Book a 30-minute call with our team to discuss your challenges directly.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Fill out the form and mention you'd like to schedule a call, and we'll send you available times.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
